// FindMeModal — client-side face recognition powered by face-api.js.
// Guest snaps a selfie → we extract a 128-dim face embedding, compare it to
// every photo in the current album, and return the media IDs that match.
// Everything runs in the browser; the selfie is never uploaded.
//
// Second visit to the same album is near-instant thanks to a per-media
// embedding cache in localStorage.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Camera, X as XIcon, RefreshCw, ScanFace, ShieldCheck, Sparkles } from "lucide-react";
import {
  loadFaceModels, extractEmbeddings, extractSingleEmbedding,
  bestDistance, getCache, saveCache, loadImage, waitForVideoReady,
} from "../lib/faceApi";
import { Media } from "../lib/api";

// face-api recommended threshold for "same person" is ~0.55–0.6.
// Slightly generous so selfie lighting differences don't hide obvious matches.
const MATCH_THRESHOLD = 0.58;

export default function FindMeModal({ open, onClose, slug, media, onMatches }) {
  const [stage, setStage] = useState("intro"); // intro | starting | camera | scanning | done | error
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [matchCount, setMatchCount] = useState(0);
  const [cameraError, setCameraError] = useState("");
  const [captureErr, setCaptureErr] = useState(""); // shown inline in the camera stage

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cancelRef = useRef(false);

  // Reset when opened / closed
  useEffect(() => {
    if (open) {
      setStage("intro");
      setStatus("");
      setProgress({ done: 0, total: 0 });
      setMatchCount(0);
      setCameraError("");
      cancelRef.current = false;
      // Warm-load models in the background so tapping "Start" feels instant.
      loadFaceModels().catch(() => {});
    }
    return () => {
      cancelRef.current = true;
      stopCamera();
    };
  }, [open]);

  const stopCamera = () => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Attach the stream AFTER the <video> exists (only rendered in "camera"
  // stage). Attaching inside startCamera raced the render and left a dead
  // preview — especially on iOS Safari.
  useEffect(() => {
    if (stage !== "camera" || !streamRef.current || !videoRef.current) return;
    const v = videoRef.current;
    v.srcObject = streamRef.current;
    v.play().catch(() => { /* playsInline+muted allows autoplay */ });
  }, [stage]);

  const startCamera = async () => {
    setCameraError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser doesn't support camera access. Try Safari 14+ or Chrome.");
      setStage("error");
      return;
    }
    setStage("starting");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = s;
      setStage("camera");
    } catch (e) {
      const denied = e?.name === "NotAllowedError" || e?.name === "SecurityError";
      setCameraError(denied
        ? "Camera permission was denied. Enable it in your browser settings (iPhone: Settings → Safari → Camera) and try again."
        : e?.message || "Couldn't access camera");
      setStage("error");
    }
  };

  const captureAndMatch = useCallback(async () => {
    if (!videoRef.current) return;
    setStage("scanning");
    setStatus("Analyzing your face…");
    setCaptureErr("");
    const v = videoRef.current;
    // iOS can report 0x0 for a moment after play() — wait for real frames.
    const ready = await waitForVideoReady(v);
    if (!ready) {
      setStatus("");
      setCaptureErr("Camera is still warming up — give it a second and try again.");
      setStage("camera");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);

    // Selfie embedding
    let selfie;
    try {
      selfie = await extractSingleEmbedding(canvas);
    } catch (e) {
      setStatus("");
      setCaptureErr("Couldn't load the face engine — check your connection and try again.");
      setStage("camera");
      return;
    }
    if (!selfie) {
      setCaptureErr("No face detected — get closer, face the camera, and make sure there's light on your face.");
      setStage("camera");
      return;
    }
    stopCamera();

    // Iterate through album media (photos only — videos are skipped for v1)
    const photos = (media || []).filter((m) => !m.is_video && !m.is_deleted);
    if (photos.length === 0) {
      setStatus("No photos to scan");
      setStage("done");
      onMatches?.([]);
      return;
    }

    setProgress({ done: 0, total: photos.length });
    setStatus("Scanning album…");

    const cache = getCache(slug);
    const matches = [];

    for (let i = 0; i < photos.length; i++) {
      if (cancelRef.current) return;
      const p = photos[i];
      try {
        let descs = cache[p.id];
        if (!descs) {
          const img = await loadImage(Media.thumbUrl(p.id));
          descs = await extractEmbeddings(img);
          cache[p.id] = descs;
          // Save every 5 photos to survive tab close mid-scan
          if (i % 5 === 0) saveCache(slug, cache);
        }
        if (descs.length > 0) {
          const dist = bestDistance(selfie, descs);
          if (dist <= MATCH_THRESHOLD) matches.push(p.id);
        }
      } catch (_) {
        // Skip unreadable photos, keep going
      }
      setProgress({ done: i + 1, total: photos.length });
      setMatchCount(matches.length);
    }

    saveCache(slug, cache);
    setStatus("");
    setStage("done");
    onMatches?.(matches);
    toast.success(
      matches.length === 0
        ? "No matches found in this album"
        : `Found you in ${matches.length} photo${matches.length === 1 ? "" : "s"}`
    );
  }, [media, slug, onMatches]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 90, background: "rgba(6,4,14,0.9)", backdropFilter: "blur(14px)" }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md p-6 rounded-3xl relative" style={{
        background: "#15131F",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9)",
      }}>
        <button
          type="button"
          onClick={() => { stopCamera(); cancelRef.current = true; onClose(); }}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }}
          data-testid="findme-close"
          aria-label="Close"
        >
          <XIcon size={16} />
        </button>

        {stage === "intro" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                   style={{ background: "rgba(255,31,107,0.16)", border: "1px solid rgba(255,31,107,0.5)" }}>
                <ScanFace size={20} color="#FF7BAA" />
              </div>
              <div>
                <div className="text-lg font-semibold">Find me in this album</div>
                <div className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
                  Snap a quick selfie — we&rsquo;ll find every photo you&rsquo;re in
                </div>
              </div>
            </div>

            <div className="rounded-2xl px-4 py-3 mb-4 text-sm flex items-start gap-3"
                 style={{ background: "rgba(34,255,183,0.08)", border: "1px solid rgba(34,255,183,0.3)", color: "rgba(255,255,255,0.85)" }}>
              <ShieldCheck size={16} color="#22FFB7" className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium" style={{ color: "#B7FFDA" }}>100% private</div>
                <div className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Face matching runs entirely in your browser. Your selfie is never uploaded or stored.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={startCamera}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base px-4 py-3"
              style={{
                background: "linear-gradient(90deg, #FF7A00 0%, #FF1F6B 100%)",
                color: "#fff",
                boxShadow: "0 14px 36px -12px rgba(255,31,107,0.55)",
              }}
              data-testid="findme-start-btn"
            >
              <Camera size={16} /> Enable camera
            </button>
          </>
        )}

        {stage === "starting" && (
          <div className="py-8 text-center" data-testid="findme-starting">
            <div className="meye-spinner mx-auto mb-4" />
            <div className="text-lg font-semibold">Starting camera…</div>
            <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>
              If your browser asks, tap &ldquo;Allow&rdquo;
            </div>
          </div>
        )}

        {stage === "camera" && (
          <>
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Sparkles size={14} color="#FFD300" />
              Center your face, then tap
            </div>
            {captureErr && (
              <div
                role="alert"
                className="text-[13px] mb-3 rounded-xl px-3 py-2"
                style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.4)", color: "#FF9B9B" }}
                data-testid="findme-capture-error"
              >
                {captureErr}
              </div>
            )}
            <div className="relative rounded-2xl overflow-hidden mb-4"
                 style={{ background: "#000", aspectRatio: "4 / 3" }}>
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ transform: "scaleX(-1)" /* mirror preview */ }}
                playsInline
                muted
                data-testid="findme-video"
              />
              <div
                className="absolute pointer-events-none"
                style={{
                  top: "12%", left: "20%", right: "20%", bottom: "12%",
                  border: "2px dashed rgba(255,255,255,0.5)",
                  borderRadius: "50%",
                }}
              />
            </div>
            <button
              type="button"
              onClick={captureAndMatch}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base px-4 py-3"
              style={{
                background: "linear-gradient(90deg, #7F4BFF 0%, #22E2FF 100%)",
                color: "#fff",
                boxShadow: "0 14px 36px -12px rgba(127,75,255,0.55)",
              }}
              data-testid="findme-capture-btn"
            >
              <Camera size={16} /> Take selfie & find me
            </button>
          </>
        )}

        {stage === "scanning" && (
          <div className="py-6 text-center">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                 style={{ background: "rgba(127,75,255,0.16)", border: "1px solid rgba(127,75,255,0.5)" }}>
              <div className="meye-spinner" />
            </div>
            <div className="text-lg font-semibold mb-1">{status || "Scanning…"}</div>
            <div className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
              {progress.total > 0
                ? `${progress.done} of ${progress.total} photos · ${matchCount} match${matchCount === 1 ? "" : "es"} so far`
                : "Reading faces from the album"}
            </div>
            {progress.total > 0 && (
              <div className="w-full h-1.5 rounded-full overflow-hidden"
                   style={{ background: "rgba(255,255,255,0.14)" }}>
                <div className="h-full rounded-full"
                     style={{
                       width: `${(progress.done / progress.total) * 100}%`,
                       background: "linear-gradient(90deg, #7F4BFF, #22E2FF)",
                       transition: "width 200ms ease",
                     }}
                />
              </div>
            )}
          </div>
        )}

        {stage === "done" && (
          <div className="py-6 text-center">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                 style={{ background: "rgba(34,255,183,0.14)", border: "1px solid rgba(34,255,183,0.5)" }}>
              <ScanFace size={26} color="#22FFB7" />
            </div>
            <div className="text-lg font-semibold mb-1">
              {matchCount === 0 ? "No matches yet" : `You're in ${matchCount} photo${matchCount === 1 ? "" : "s"}`}
            </div>
            <div className="text-sm mb-5" style={{ color: "rgba(255,255,255,0.6)" }}>
              {matchCount === 0
                ? "Check back after more uploads or try again with better lighting."
                : "The gallery is filtered — tap any tile to view."}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-4 py-3"
              style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
              data-testid="findme-done-close"
            >
              Close
            </button>
          </div>
        )}

        {stage === "error" && (
          <div className="py-4 text-center">
            <div className="text-lg font-semibold mb-1">Camera not available</div>
            <div className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
              {cameraError || "Please grant camera permission and try again."}
            </div>
            <button
              type="button"
              onClick={() => setStage("intro")}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-4 py-3"
              style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
              data-testid="findme-retry"
            >
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
